import { Checklist } from "@/bryntum-lib/models/Checklist";
import { useCallback, useMemo } from "react";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { duplicateChecklist, importChecklistItemsApi, exportChecklistItemsApi } from "@/network/network";
import { toast } from "sonner";
import { useConfirm } from "@omit/react-confirm-dialog";
import { copyTextToClipboard } from "@/other/utils/utils";
import { DEEP_LINK_URL_KEYS } from "@/other/constants/OtherKeys";
import { getEntityShareLink, getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { getSavedBoardSelection } from "../useBoards";
import { EntityRecord } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ISwivuStore } from "@/types/SwivuStore";

export type TUseSwivuChecklistReturn = ReturnType<typeof useSwivuChecklist>;

export const useSwivuChecklist = ({ record, version }: { record: EntityRecord; version?: number }) => {
  const context = useSwivuBryntumContext();
  const { project, user } = context;
  const checklistStore = project.checklistStore;
  const confirm = useConfirm();

  const checklists = useMemo(() => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    return checklistStore.query((checklist: Checklist) => checklist[record.entityType] === record.id && (canShowArchivedRecords || !checklist.archived)) as unknown as Checklist[];
  }, [record, version]);

  const getLastItemOrder = useCallback((store: ISwivuStore<Checklist>) => {
    return store.last?.order || 0;
  }, []);

  const canEditChecklist = useCallback(
    (checklist: Checklist) => {
      return user.can(checklist.canEdit);
    },
    [user]
  );

  const createChecklist = useCallback(
    (title: string = "", assignedBoardUsers: number[] = []) => {
      if (!record || record.isPhantom) return null;

      const order = getLastItemOrder(checklistStore) + 1;
      const checklistRecord = checklistStore.createRecord({
        title,
        [record.entityType]: record.id,
        archived: record.archived,
        canEdit: GRANTS.grant_five,
        canView: GRANTS.grant_six,
        order,
        assignedBoardUsers
      });

      checklistStore.add(checklistRecord);
      return checklistRecord;
    },
    [record, checklistStore, getLastItemOrder]
  );

  // Helper to check if a checklist is a phantom record (not yet synced to backend)
  const isPhantomChecklist = useCallback((checklist: Checklist) => {
    return !checklist.id || String(checklist.id).startsWith("_generated");
  }, []);

  // Update checklist
  const updateChecklist = useCallback(
    (checklist: Checklist, updates: Partial<Checklist>) => {
      // Skip permission check for phantom records (newly created, not yet synced)
      // Phantom records are always created by the current user
      if (!isPhantomChecklist(checklist)) {
        if (!user.can(checklist.canEdit)) {
          toast.error("Permission Denied", {
            description: "You don't have permission to edit this checklist"
          });
          return false;
        }
      }

      Object.assign(checklist, updates);
      return true;
    },
    [user, isPhantomChecklist]
  );

  // Delete checklist
  const deleteChecklist = useCallback(
    async (checklist: Checklist, onSuccess?: () => void): Promise<boolean> => {
      if (!user.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to delete this checklist"
        });
        return false;
      }

      const isDelete = await confirm({
        title: `Delete checklist "${checklist.title}"`,
        description: `Are you sure you want to delete the checklist "${checklist.title}"?`,
        confirmText: "Delete",
        cancelButton: {
          variant: "ghost"
        },
        confirmButton: {
          className: "dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors"
        }
      });

      if (isDelete) {
        checklistStore.remove(checklist.id);
        onSuccess?.();
        toast.success("Checklist Deleted", {
          description: `"${checklist.title}" has been successfully deleted`
        });
      }
      return isDelete;
    },
    [user]
  );

  // Archive/unarchive checklist
  const toggleArchiveChecklist = useCallback(
    (checklist: Checklist): boolean => {
      if (!user.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to archive this checklist"
        });
        return false;
      }

      const wasArchived = checklist.archived;
      checklist.archived = !checklist.archived;

      if (wasArchived) {
        toast.success("Checklist Unarchived", {
          description: `"${checklist.title}" has been unarchived`
        });
      } else {
        toast.success("Checklist Archived", {
          description: `"${checklist.title}" has been archived`
        });
      }

      return true;
    },
    [user]
  );

  // Duplicate checklist
  const duplicateChecklistRecord = useCallback(
    (checklist: Checklist) => {
      if (!user.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to duplicate this checklist"
        });
        return false;
      }

      const order = getLastItemOrder(checklistStore) + 1;
      const checklistPhantomRecord = checklistStore.createRecord({
        [record.entityType]: record.id,
        title: `Copy of ${checklist.title}`,
        archived: checklist.archived,
        canEdit: checklist.canEdit,
        canView: checklist.canView,
        order
      });

      checklistStore.add(checklistPhantomRecord);

      const detacher = checklistStore.on("idChange", () => {
        duplicateChecklist(checklistPhantomRecord.id, {
          [record.entityType]: record.id,
          archived: checklist.archived,
          canEdit: checklist.canEdit,
          canView: checklist.canView,
          order,
          duplicate_id: checklist.id
        })
          .then(({ data }) => {
            project.applyChangeset({
              [STORE_KEYS.checklistStore]: { added: data[STORE_KEYS.checklistStore].rows },
              [STORE_KEYS.checklistItemStore]: { added: data[STORE_KEYS.checklistItemStore].rows }
            });
            toast.success("Checklist Duplicated", {
              description: `"${checklist.title}" has been successfully duplicated`
            });
          })
          .catch(() => {
            toast.error("Duplication Failed", {
              description: `Failed to duplicate "${checklist.title}"`
            });
          });
        detacher();
      });

      return true;
    },
    [checklistStore, user, record, project]
  );

  // Import checklist items from file
  const importChecklistItems = useCallback(
    (checklist: Checklist, file: File) => {
      if (!user.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to import items to this checklist"
        });
        return;
      }

      if (file && file.type === "text/plain") {
        const fileReader = new FileReader();
        fileReader.onload = async ({ target }) => {
          const checklistItemsData = target?.result as string;
          try {
            const { data } = await importChecklistItemsApi(checklist.id, checklistItemsData);
            project.applyChangeset({
              [STORE_KEYS.checklistItemStore]: { added: data[STORE_KEYS.checklistItemStore].rows }
            });
            toast.success("Import Successful", {
              description: `Items imported successfully to "${checklist.title}"`
            });
          } catch (error) {
            console.error("Import error:", error);
            toast.error("Import Failed", {
              description: `Failed to import items to "${checklist.title}"`
            });
          }
        };
        fileReader.readAsText(file);
      } else {
        toast.error("Unsupported File Type", {
          description: "Please upload files with extensions: .txt"
        });
      }
    },
    [user, project]
  );

  // Export checklist items
  const exportChecklistItems = useCallback(async (checklist: Checklist) => {
    try {
      await exportChecklistItemsApi(checklist.id);
      toast.success("Export Successful", {
        description: `Items from "${checklist.title}" exported successfully`
      });
      return true;
    } catch {
      toast.error("Export Failed", {
        description: `Failed to export items from "${checklist.title}"`
      });
      return false;
    }
  }, []);

  const copyChecklistLink = useCallback(
    (checklist: Checklist) => {
      // Determine entity type based on entityType property instead of isTask/isResource flags
      const deepLinkEntityType =
        record.entityType === 'taskId' ? DEEP_LINK_URL_KEYS.task :
        record.entityType === 'resourceId' ? DEEP_LINK_URL_KEYS.resource :
        DEEP_LINK_URL_KEYS.client;

      const { linkOnly } = getEntityShareLink({
        record: record,
        context: context,
        editorTabs: "checklist",
        entityType: deepLinkEntityType,
        otherKeys: { [DEEP_LINK_URL_KEYS.checklist]: checklist.id },
        entityDetail: { name: checklist.title }
      });
      copyTextToClipboard(linkOnly, `Link to ${checklist.title} copied to clipboard successfully!`);
    },
    [project, user, record]
  );

  return {
    checklists,
    createChecklist,
    updateChecklist,
    deleteChecklist,
    toggleArchiveChecklist,
    duplicateChecklistRecord,
    importChecklistItems,
    exportChecklistItems,
    canEditChecklist,
    copyChecklistLink,
    checklistStore
  };
};
